import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminListCompanies, adminCreateCompany } from '../../lib/adminApi';
import type { AdminCompanyListRow } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';

/*
 * Velaigal /admin/companies — the tenant directory.
 *
 * Companies are admin-provisioned: there is no self-serve creation and no invite flow, so
 * this page is where a tenant starts existing. The create panel is collapsed by default
 * because provisioning is rare and browsing is not. Search and pagination mirror Users.tsx
 * exactly (offset paging via page/hasMore, on-submit search, both resetting to page 1).
 *
 * Rows are read-only; editing a company and every membership change live one level down on
 * CompanyDetail (/admin/companies/:id), which this list only links into.
 */

// Status chips are informational, never the accent state, so they take the neutral hairline
// override — saffron stays reserved for a genuinely live/positive signal. Mirrors
// NEUTRAL_CHIP_STYLE in src/pages/Admin/Users.tsx:36.
//
// The seat chip says "active" out loud because this endpoint's memberCount is
// `COUNT(*) ... WHERE status = 'active'` while GET /api/admin/companies/:id returns EVERY member
// regardless of status (both in server/src/routes/adminCompanies.ts). The two numbers are meant
// to disagree; the detail page restates it as "N active of M listed".
const NEUTRAL_CHIP_STYLE = { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' } as const;

const LoadingBlock: React.FC = () => (
  <div role="status" className="py-16 text-center">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-6 text-sm text-ink-tertiary">Loading companies.</p>
  </div>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">Couldn&apos;t load companies.</h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const EmptyBlock: React.FC = () => (
  <div className="bg-paper border border-hairline rounded-md p-8 text-center">
    <p className="text-ink">No companies yet.</p>
    <p className="text-sm text-ink-secondary mt-2">Provision one above, then attach its recruiters.</p>
  </div>
);

// border-cream is invisible on a dark .btn-primary fill (CLAUDE.md, design-system rule 1):
// the ring is translucent cream with a solid cream top edge.
const InlineSpinner: React.FC<{ label: string }> = ({ label }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={{ borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }}
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

const CompanyRow: React.FC<{ company: AdminCompanyListRow }> = ({ company }) => (
  <Link to={`/admin/companies/${company.id}`} className="card block no-underline text-ink">
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="chip" style={NEUTRAL_CHIP_STYLE}>
            {company.status === 'active' ? 'Active' : 'Suspended'}
          </span>
          <span className="chip" style={NEUTRAL_CHIP_STYLE}>
            {company.memberCount} active {company.memberCount === 1 ? 'recruiter' : 'recruiters'}
          </span>
        </div>
        <p className="font-display text-lg font-medium text-ink">{company.name}</p>
        <p className="text-sm text-ink-secondary mt-0.5">{company.slug}</p>
      </div>
      <span className="text-xs text-ink-tertiary flex-shrink-0">Added {formatRelativeDate(company.createdAt)}</span>
    </div>
  </Link>
);

interface CreatePanelProps {
  onCreated: (company: AdminCompanyListRow) => void;
}

const CreatePanel: React.FC<CreatePanelProps> = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    try {
      const { company } = await adminCreateCompany({
        name: name.trim(),
        website: website.trim(),
        description: description.trim(),
      });
      // The list endpoint carries memberCount; a brand-new company has no seats yet.
      onCreated({ ...company, memberCount: 0 });
      toast.success(`${company.name} provisioned.`);
      setName('');
      setWebsite('');
      setDescription('');
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create that company.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn-primary mb-6" onClick={() => setOpen(true)}>
        New company
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card mb-6">
      <span className="label-editorial block mb-4">New company</span>
      <div className="space-y-4">
        <div>
          <label className="label-editorial block mb-1" htmlFor="company-name">
            Name
          </label>
          <input
            id="company-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Robotics"
            maxLength={200}
            required
          />
          <p className="text-xs text-ink-tertiary mt-1">
            The slug is derived from this. No screen can change it afterward — the API accepts a new one, the UI never sends it.
          </p>
        </div>
        <div>
          <label className="label-editorial block mb-1" htmlFor="company-website">
            Website
          </label>
          <input
            id="company-website"
            className="input"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://acme.dev"
            maxLength={300}
          />
        </div>
        <div>
          <label className="label-editorial block mb-1" htmlFor="company-description">
            Description
          </label>
          <textarea
            id="company-description"
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this company builds — this feeds the task-generation prompt."
            maxLength={4000}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-6">
        <button type="submit" className="btn-primary" disabled={busy || !name.trim()}>
          {busy ? <InlineSpinner label="Creating…" /> : 'Create company'}
        </button>
        <button type="button" className="btn-secondary" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
};

const Companies: React.FC = () => {
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const [companies, setCompanies] = useState<AdminCompanyListRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't setState
  // afterward — mirrors the load effect in src/pages/Admin/Users.tsx.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await adminListCompanies({ q: q || undefined, page });
        if (cancelled) return;
        setCompanies(res.companies);
        setHasMore(res.hasMore);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q, page, loadNonce]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(qInput.trim());
    setPage(1);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">Admin</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">Companies</h1>

      <CreatePanel onCreated={() => setLoadNonce((n) => n + 1)} />

      <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 mb-6">
        <input
          type="text"
          className="input"
          placeholder="Search name or slug"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          maxLength={200}
          style={{ maxWidth: 240 }}
          aria-label="Search companies"
        />
        <button type="submit" className="btn-secondary" style={{ padding: '0.5rem 1rem' }}>
          Search
        </button>
      </form>

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock message={error} onRetry={() => setLoadNonce((n) => n + 1)} />
      ) : companies.length === 0 ? (
        <EmptyBlock />
      ) : (
        <>
          <div className="space-y-4">
            {companies.map((company) => (
              <CompanyRow key={company.id} company={company} />
            ))}
          </div>

          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="text-xs text-ink-tertiary">Page {page}</span>
            <button type="button" className="btn-secondary" onClick={() => setPage((p) => p + 1)} disabled={!hasMore}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Companies;
